import React, { useCallback, useState, useEffect, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CategoryModal from "./parentCategoryModel";
import CategoryListIcon from "../CategoryListIcon/CategoryListIcon";

const CategorySelector = ({
  selectedCategories,
  setSelectedCategories,
  showModal,
  setShowModal,
  setLoading,
  onModalSubmit, // Callback when modal is submitted
  hideDisplay = false, // Hide the display area when true
}) => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const isDarkMode = useDarkMode();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Store setLoading in a ref to prevent re-renders
  const setLoadingRef = useRef(setLoading);
  useEffect(() => {
    setLoadingRef.current = setLoading;
  }, [setLoading]);

  const fetchCategories = useCallback(async (options = {}) => {
    const showGlobalLoading = options.showGlobalLoading !== false;
    if (!CompID) return;

    try {
      setCategoriesLoading(true);
      if (showGlobalLoading) setLoadingRef.current(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });

      const categoryData = response.data.data || response.data;
      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
      } else {

        setCategories([]);
      }
    } catch (err) {

      setCategories([]);
    } finally {
      setCategoriesLoading(false);
      if (showGlobalLoading) setLoadingRef.current(false);
    }
  }, [CompID]);

  

  const handleModalSubmit = (selected) => {
    setSelectedCategories(selected);
    setShowModal(false);
    // Call parent's onModalSubmit callback if provided
    if (typeof onModalSubmit === 'function') {
      onModalSubmit(selected);
    }
  };

  return (
    <>
      {!hideDisplay && (
      <div className="mt-3">
        {Array.isArray(selectedCategories) && selectedCategories.length > 0 ? (
          <>
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
              {selectedCategories.map((group, index) => (
                <div key={group?.rootCat_ID || group?.rootCat_Name || index} className="col">
                  <div 
                    className="category-chip"
                    style={{
                      background: isDarkMode ? '#404040' : '#ffffff',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#007bff';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
                      e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <CategoryListIcon
                          src={group?.rootCat_Image}
                          alt={group?.rootCat_Name}
                          size={26}
                          boxSize={32}
                          className="me-2"
                        />

                        {/* Category Name */}
                        <div>
                          <span 
                            style={{ 
                              fontSize: '15px', 
                              fontWeight: '500',
                              color: isDarkMode ? '#ffffff' : '#495057'
                            }}
                          >
                            {group?.rootCat_Name || 'Unknown Category'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Remove Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategories((prev) =>
                            prev.filter((g, i) => i !== index)
                          );
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          color: '#dc3545'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isDarkMode ? '#4a1a1a' : '#f8d7da';
                          e.currentTarget.style.color = isDarkMode ? '#ff6b6b' : '#721c24';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#dc3545';
                        }}
                      >
                        <i 
                          className="fa fa-times" 
                          style={{ 
                            fontSize: '12px'
                          }}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center mt-4">
              <button
                onClick={() => setShowModal(true)}
                style={{
                  background: themeColor,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = themeColor + 'dd';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = themeColor;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                Add More Category
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div 
              style={{
                width: '60px',
                height: '60px',
                background: isDarkMode ? '#555' : '#f8f9fa',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
              }}
            >
              <i className="fa fa-tags" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
            </div>
            <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>No Category selected</p>
            <button
              onClick={() => setShowModal(true)}
              style={{
                background: themeColor,
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = themeColor + 'dd';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = themeColor;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
              Add Category
            </button>
          </div>
        )}
      </div>
      )}

      <CategoryModal
        showParentCategoryModal={showModal}
        setShowParentCategoryModal={setShowModal}
        showButton={true}
        setSelectedParentCategory={setSelectedCategories}
        selectedParentCategory={selectedCategories}
        onSubmit={handleModalSubmit}
        categories={categories}
        isMultiSelect={true}
        modalTitle="Select Category"
        modalSubTitle="Select the categories for the item"
        fetchCategories={fetchCategories}
        categoriesLoading={categoriesLoading}
      />
    </>
  );
};

export default CategorySelector;